import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';

import ClassroomPage from '../pages/ClassroomPage';
import CoursePage from '../pages/CoursePage';
import CoursesPage from '../pages/CoursesPage';
import ContentResetWatcher from './ContentResetWatcher';
import AIUniversityLanding from '../pages/landingPage'; // Import Landing Page
import AnalyticsPage from '../pages/AnalyticsPage';
import Dashboard from '../pages/Dashboard';
import CourseCreationForm from '../forms/CourseCreationForm';
import CourseCreationChatPage from '../pages/CourseCreationChatPage';
import ProfilePage from '../pages/ProfilePage';
import ReflectionPage from '../pages/ReflectionPage';
import ReflectionEntryPage from '../pages/ReflectionEntryPage';
import ReflectionQuestionsPreferences from '../pages/ReflectionQuestionsPreferences';
import AgentPersonasPreferences from '../pages/AgentPersonasPreferences';
import LibraryPage from '../pages/LibraryPage';
import VideoWatchPage from '../pages/VideoWatchPage';
import ProjectsPage from '../pages/ProjectsPage';
import ProjectDetailsPage from '../pages/ProjectDetailsPage';
import PromptsPage from '../pages/PromptsPage';
import PromptGraphPage from '../pages/PromptGraphPage';
import LessonDemo from '../pages/LessonDemo';
import SceneLessonPage from '../pages/SceneLessonPage';
import PublicSceneDemoPage from '../pages/PublicSceneDemoPage';
import CourseExamsPage from '../pages/CourseExamsPage';
import Sidebar from '../components/ui/Sidebar';
import TasksSidebar from '../components/Tasks/TasksSidebar';
import Breadcrumb from '../components/ui/Breadcrumb';
import Login from '../pages/auth/login';
import SignUp from '../pages/auth/signUp';
import ResetPassword from '../pages/auth/resetPassword';
import UpdatePassword from '../pages/auth/updatePassword';
import RedirectResetPassword from '../pages/auth/redirectResetPassword';
import VerificationSent from '../pages/auth/verificationSent';
import ConfirmEmail from '../pages/auth/accountConfirmation';
import ProtectedRoute from './ProtectedRoutes';
import { selectAuthLoading } from '../redux/authSlice';
import LoadingSpinner from '../components/ui/LoadingSpinner';

// Wrapper component to use useLocation hook
const AppContent = () => {
  // Removed unused route flags; routing handled directly via <Routes>

  const loading = useSelector(selectAuthLoading);
  const location = useLocation();

  // Sidebar open/close state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((v) => !v);
  const closeSidebar = () => setSidebarOpen(false);

  // Tasks sidebar open/close state
  const [tasksSidebarOpen, setTasksSidebarOpen] = useState(false);
  const toggleTasksSidebar = () => setTasksSidebarOpen((v) => !v);
  const closeTasksSidebar = () => setTasksSidebarOpen(false);

  // Route transition animation
  const contentRef = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || loading) return;

    // Reset animation
    el.classList.remove('is-enter');
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';

    // Trigger reflow
    el.getBoundingClientRect();

    // Animate in
    requestAnimationFrame(() => {
      el.classList.add('is-enter');
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, [location.pathname, loading]);

  // Only show sidebar for protected paths
  const isProtectedPath =
    /^\/(dashboard|analytics|classroom|course-exams|create-course|create-course\/ai|profile|courses|reflection|library|video|projects|prompts|prompt-graph|preferences)(\/|$)?/.test(
      location.pathname
    );

  if (loading) {
    // 🛑 Don't render routes until auth is ready
    return <LoadingSpinner />;
  }

  return (
    <>
      <ContentResetWatcher />
      <div className="ml-app-shell" style={{ display: 'flex', minHeight: '100vh' }}>
        {isProtectedPath && (
          <>
            <Sidebar open={sidebarOpen} onClose={closeSidebar} onOpenTasks={toggleTasksSidebar} />
            <TasksSidebar
              open={tasksSidebarOpen}
              onClose={closeTasksSidebar}
              onToggle={toggleTasksSidebar}
            />
          </>
        )}
        <div className="ml-app-shell__content">
          {isProtectedPath && (
            <Breadcrumb
              onOpenTasks={toggleTasksSidebar}
              onToggleSidebar={toggleSidebar}
              sidebarOpen={sidebarOpen}
            />
          )}
          <div
            ref={contentRef}
            className="container-fluid p-0 ml-app-shell__panel"
            style={{
              transition:
                'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1), transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
              willChange: 'transform, opacity',
            }}
          >
            <Routes>
              {/* Landing Page Route */}
              <Route path="/" element={<AIUniversityLanding />} />
              <Route path="/try" element={<PublicSceneDemoPage />} />
              <Route path="/demo" element={<PublicSceneDemoPage />} />

              {/* Lesson Demo Route */}
              <Route path="/lesson-demo" element={<LessonDemo />} />
              <Route path="/lesson-demo/scene" element={<SceneLessonPage />} />

              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/redirect-reset-password" element={<RedirectResetPassword />} />
              <Route path="/verification-sent" element={<VerificationSent />} />
              <Route path="/confirm-email" element={<ConfirmEmail />} />

              <Route path="/courses/:courseId" element={<CoursePage />} />

              <Route element={<ProtectedRoute />}>
                {/* Profile Route */}
                <Route path="/profile" element={<ProfilePage />} />
                {/* Course Creation Routes */}
                <Route path="/create-course" element={<CourseCreationForm />} />
                <Route path="/create-course/ai" element={<CourseCreationChatPage />} />

                {/* Classroom Page Route */}
                <Route path="/classroom" element={<ClassroomPage />} />
                <Route path="/course-exams" element={<CourseExamsPage />} />

                {/* Dashboard Route */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/analytics" element={<AnalyticsPage />} />

                {/* Browse All Courses Route */}
                <Route path="/courses" element={<CoursesPage />} />

                {/* Library Route */}
                <Route path="/library" element={<LibraryPage />} />

                {/* Video Watch Route */}
                <Route path="/video/:videoId" element={<VideoWatchPage />} />

                {/* Projects Routes */}
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:projectId" element={<ProjectDetailsPage />} />
                <Route path="/prompts" element={<PromptsPage />} />
                <Route path="/prompt-graph" element={<PromptGraphPage />} />

                {/* Reflection Routes */}
                <Route path="/reflection" element={<ReflectionPage />} />
                <Route path="/reflection/:date" element={<ReflectionEntryPage />} />

                {/* Preferences Routes */}
                <Route
                  path="/preferences/reflection-questions"
                  element={<ReflectionQuestionsPreferences />}
                />
                <Route path="/preferences/agent-personas" element={<AgentPersonasPreferences />} />
              </Route>
            </Routes>
          </div>
        </div>
      </div>
    </>
  );
};

// Main AppRoutes component
function AppRoutes() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default AppRoutes;
